import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import ShareButton from "@/components/feature/ShareButton";
import FavoriteButton from "@/components/feature/FavoriteButton";
import RecommendCard from "@/components/feature/RecommendCard";
import usePageMeta from "@/hooks/usePageMeta";
import {
  getRecommendById,
  getCharactersByWorkId,
  categoryMeta,
} from "@/lib/content";
import { recommendList } from "@/mocks/recommend";
import { FAV_RECOMMEND_KEY } from "@/lib/favorites";
import type { RecommendItem } from "@/types/content";

export default function RecommendDetail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getRecommendById(id) : undefined;

  usePageMeta({
    title: item ? `${item.title} - 香蕉动漫` : "该番剧不存在 - 香蕉动漫",
    description: item ? item.summary : undefined,
    keywords: item ? `${item.title},${item.genres.join(",")},番剧推荐,香蕉动漫` : undefined,
  });

  if (!item) {
    return (
      <div className="w-full px-4 md:px-6 py-10">
        <EmptyState
          icon="ri-film-line"
          title="该番剧不存在或已下架"
          description="你访问的番剧推荐可能已被移除或链接有误，看看其他精彩推荐吧。"
          linkTo="/category/recommend"
          linkLabel="浏览番剧推荐"
        />
      </div>
    );
  }

  const related = (recommendList as RecommendItem[])
    .filter((r) => r.id !== item.id)
    .filter((r) => r.genres.some((g) => item.genres.includes(g)))
    .concat((recommendList as RecommendItem[]).filter((r) => r.id !== item.id))
    .filter((r, index, arr) => arr.findIndex((x) => x.id === r.id) === index)
    .slice(0, 4);

  const characters = getCharactersByWorkId(item.id);
  const infoRows = [
    { label: "类型", value: item.genres.join(" / ") },
    { label: "地区", value: item.region },
    { label: "年份", value: item.year },
    { label: "状态", value: item.status },
    { label: "集数", value: item.episodes },
  ];

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb
        items={[
          { label: categoryMeta.recommend.title, to: "/category/recommend" },
          { label: item.title },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-6 lg:gap-8">
        <div className="w-full max-w-[280px]">
          <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden border border-background-200">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} - 香蕉动漫`}
              className="w-full h-full object-cover object-top"
            />
            <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-md bg-black/55 px-2 py-1 text-sm font-bold text-primary-400">
              <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i>
              {item.score}
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <FavoriteButton storageKey={FAV_RECOMMEND_KEY} id={item.id} label="收藏番剧" />
            <ShareButton title={item.title} />
          </div>
          <p className="mt-2 text-xs text-foreground-400 leading-relaxed">
            收藏仅保存在当前设备与浏览器中，清除浏览器数据后会丢失。
          </p>
        </div>

        <div className="min-w-0">
          <h1 className="font-display font-black text-2xl md:text-3xl lg:text-4xl text-foreground-950 leading-snug">
            {item.title}
          </h1>
          <div className="mt-3 flex flex-wrap gap-2">
            {item.genres.map((g) => (
              <span
                key={g}
                className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-accent-700"
              >
                {g}
              </span>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {infoRows.map((row) => (
              <div
                key={row.label}
                className="rounded-lg border border-background-200 bg-background-50 px-3 py-2.5"
              >
                <p className="text-xs text-foreground-400">{row.label}</p>
                <p className="mt-0.5 text-sm font-medium text-foreground-900">{row.value}</p>
              </div>
            ))}
          </div>

          <section className="mt-7">
            <h2 className="font-display font-bold text-lg text-foreground-950 mb-2 flex items-center gap-2">
              <i className="ri-book-open-line w-5 h-5 flex items-center justify-center text-accent-600"></i>
              故事简介
            </h2>
            <p className="text-sm md:text-base leading-8 text-foreground-700">{item.summary}</p>
          </section>

          <section className="mt-6">
            <h2 className="font-display font-bold text-lg text-foreground-950 mb-2 flex items-center gap-2">
              <i className="ri-thumb-up-line w-5 h-5 flex items-center justify-center text-accent-600"></i>
              推荐理由
            </h2>
            <div className="rounded-lg border-l-4 border-primary-500 bg-primary-50 px-4 py-3 text-sm md:text-base leading-7 text-foreground-700">
              {item.reason}
            </div>
          </section>

          <section className="mt-6">
            <h2 className="font-display font-bold text-lg text-foreground-950 mb-2 flex items-center gap-2">
              <i className="ri-list-check w-5 h-5 flex items-center justify-center text-accent-600"></i>
              剧情梗概
            </h2>
            <div className="space-y-3 mt-2">
              {item.content.map((paragraph, index) => (
                <p key={index} className="text-sm md:text-base leading-8 text-foreground-700">
                  {paragraph}
                </p>
              ))}
            </div>
          </section>

          {characters.length > 0 && (
            <section className="mt-7">
              <h2 className="font-display font-bold text-lg text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-user-star-line w-5 h-5 flex items-center justify-center text-accent-600"></i>
                主要角色
              </h2>
              <div className="flex flex-wrap gap-3">
                {characters.map((c) => (
                  <Link
                    key={c.id}
                    to={`/detail/character/${c.id}`}
                    className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-2 pr-4 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="w-12 h-14 rounded-md overflow-hidden shrink-0">
                      <img
                        src={c.image}
                        alt={c.name}
                        title={`${c.name} - 香蕉动漫`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 group-hover:text-accent-600 transition-colors">
                        {c.name}
                      </p>
                      <p className="text-xs text-foreground-500">配音：{c.voiceActor}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <Link
            to="/category/recommend"
            className="mt-7 flex items-center justify-between rounded-lg bg-secondary-100 px-4 py-3 text-sm font-medium text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer"
          >
            返回番剧推荐列表
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </Link>
        </div>
      </div>

      <section className="mt-12">
        <h2 className="font-display font-black text-xl md:text-2xl text-foreground-950 mb-5 flex items-center gap-3">
          <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
          相关番剧推荐
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {related.map((r) => (
            <RecommendCard key={r.id} item={r} />
          ))}
        </div>
      </section>
    </div>
  );
}